/* eslint-disable react/require-default-props,react/default-props-match-prop-types */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Moment from 'moment';

import DateTime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';

import './DateTimePicker.scss';

import { noop } from '../../utilities/functions';
import { TextField } from '../TextField/component';

/*
 * Wrapper for react-datetime for a consistent look/feel across OL.
 *
 * For simplicity:
 * - Only takes in Date instance
 * - Only available as controlled component
 *
 *
 * Full options: https://github.com/YouCanBookMe/react-datetime
 */
export class DateTimePicker extends React.PureComponent {
  renderInput = (props) => {
    const {
      className,
      blockManualInput,
      disableClear,
      ...textFieldProps
    } = this.props;

    const allowedKeyCodes = [
      9, // tab
      37, // left arrow
      38, // up arrow
      39, // right arrow
      40, // down arrow
    ];

    const clear = () => {
      if (!textFieldProps.disabled) {
        props.onChange({ target: { value: '' } });
      }
    };

    const blockInput = (event) => {
      if (blockManualInput && !allowedKeyCodes.includes(event.keyCode)) {
        event.preventDefault();
      }
      props.onKeyDown(event);
    };

    const button = disableClear
      ? null
      : {
          label: 'Clear',
          onClick: clear,
        };

    return (
      <div className={classNames('datetimepicker', className)}>
        <TextField
          {...textFieldProps}
          {...props}
          onKeyDown={blockInput}
          button={button}
          autoComplete="off"
          inline={false}
        />
      </div>
    );
  };

  onChange = (value) => {
    const { onChange } = this.props;

    if (typeof value === 'string') {
      // The input is not valid
      // For some reason, the empty string is the standard error value...
      onChange('');
      return;
    }
    onChange(value);
  };

  render() {
    const {
      utc,
      value,
      dateFormat,
      timeFormat,
      closeOnSelect,
      onFocus,
      onBlur,
      renderDay,
      renderMonth,
      renderYear,
      isValidDate,
    } = this.props;

    return (
      <DateTime
        {...{
          utc,
          value,
          dateFormat,
          timeFormat,
          closeOnSelect,
          onFocus,
          onBlur,
          renderDay,
          renderMonth,
          renderYear,
          isValidDate,
        }}
        onChange={this.onChange}
        renderInput={this.renderInput}
      />
    );
  }
}

DateTimePicker.propTypes = {
  // config
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(Moment),
    PropTypes.string,
  ]),
  dateFormat: PropTypes.string.isRequired,
  timeFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    .isRequired,
  className: PropTypes.string,
  closeOnSelect: PropTypes.bool,

  // input
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  icon: PropTypes.string,
  description: PropTypes.node,
  error: PropTypes.string,
  autoSave: PropTypes.bool,
  autoSaveState: PropTypes.string,

  // events
  onChange: PropTypes.func.isRequired, // (momentValue) => {}
  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,

  // custom rendering
  renderDay: PropTypes.func,
  renderMonth: PropTypes.func,
  renderYear: PropTypes.func,

  // selectable dates
  isValidDate: PropTypes.func,

  blockManualInput: PropTypes.bool,

  disabled: PropTypes.bool,

  utc: PropTypes.bool,

  disableClear: PropTypes.bool,
};

DateTimePicker.defaultProps = {
  value: new Date(),
  dateFormat: 'DD MMM YYYY',
  timeFormat: 'h:mm a', // space between "mm a" makes it possible to select between AM/PM in the UI
  closeOnSelect: false,
  className: null,

  placeholder: 'Select a date',
  required: false,
  icon: null,
  description: null,
  error: null,
  autoSave: false,
  autoSaveState: null,

  onChange: noop,
  onFocus: noop,
  onBlur: noop,

  renderDay: undefined,
  renderMonth: undefined,
  renderYear: undefined,

  isValidDate: () => true,

  blockManualInput: false,

  disabled: false,

  utc: false,

  disableClear: false,
};
